import { useCallback, useEffect, useRef, useState } from "react";

import { accessApi, useAccessState } from "../features/access/api";
import { endSession } from "./api-client";
import { lastActivityAt, subscribeActivity } from "./session-activity";
import {
  DEFAULT_WARNING_LEAD_SECONDS,
  epochSeconds,
  idleEdgeOf,
  sessionWarning,
  shouldKeepalive,
  type SessionWarning,
} from "./session-expiry";

/**
 * [NF-008] / [AC-062] in the SPA: announce the end of the session
 * `SESSION_WARNING_LEAD` ahead, keep an active person from reaching the idle
 * edge, and drop to the login the moment the session is gone.
 *
 * The session is read from the module's `GET /access/access` with
 * `X-Background: 1` - once a minute and on focus - so the check itself never
 * counts as activity. Activity is what the person's own requests already are;
 * real input past half of the idle window adds one plain request, because a
 * person typing a long postmortem makes no request for a long time and is not
 * idle ([AC-030]).
 *
 * The policy lives in `session-expiry.ts` and is unit-tested there; this hook
 * only wires it to the clock, the DOM and the two requests.
 */

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

export interface SessionExpiryState {
  /** The warning due now, or null. */
  warning: SessionWarning | null;
  /** Reset the idle limit now - what the banner's extend button calls. */
  extend: () => void;
}

export function useSessionExpiry(enabled: boolean): SessionExpiryState {
  const state = useAccessState(enabled);
  const [activity, setActivity] = useState<number | null>(() => lastActivityAt());
  const [nowSeconds, setNowSeconds] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => subscribeActivity(() => setActivity(lastActivityAt())), []);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setNowSeconds(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [enabled]);

  // The module says the session is gone: past its idle edge, revoked, or
  // ended at its absolute end. No 401 carries that news on a background read.
  const authenticated = state.data?.authenticated;
  useEffect(() => {
    if (enabled && authenticated === false) endSession();
  }, [enabled, authenticated]);

  const idleSeconds = state.data?.session_idle_seconds ?? null;
  const clock = {
    endsAt: epochSeconds(state.data?.session_ends_at),
    idleEdge: idleEdgeOf(activity, idleSeconds),
    lead: state.data?.session_warning_lead ?? DEFAULT_WARNING_LEAD_SECONDS,
  };

  // One reset at a time: a burst of key presses is one request, not twenty.
  const extending = useRef(false);
  const extend = useCallback(() => {
    if (extending.current) return;
    extending.current = true;
    // A plain request: it resets the idle limit and is noted as activity.
    void accessApi
      .state()
      .catch(() => undefined)
      .finally(() => {
        extending.current = false;
      });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const onActivity = (): void => {
      const now = Math.floor(Date.now() / 1000);
      if (shouldKeepalive(idleEdgeOf(lastActivityAt(), idleSeconds), idleSeconds, now)) extend();
    };
    for (const event of ACTIVITY_EVENTS) window.addEventListener(event, onActivity, { passive: true });
    return () => {
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, onActivity);
    };
  }, [enabled, idleSeconds, extend]);

  return {
    warning: enabled && state.data?.authenticated ? sessionWarning(clock, nowSeconds) : null,
    extend,
  };
}
